import { CreditCard, FileStack, Globe, FileText, Package, Settings, Sparkles, Users } from 'lucide-react';
import { Link, useLocation, useParams } from 'react-router-dom';

interface BrandTab {
    label: string;
    path: string;
    icon: React.ElementType;
    matchPaths?: string[];
}

export function BrandTabs() {
    const { identifier } = useParams<{ identifier: string }>();
    const location = useLocation();

    if (!identifier) return null;

    const basePath = `/brands/${identifier}`;

    const tabs: BrandTab[] = [
        {
            label: 'Overview',
            path: basePath,
            icon: Settings,
        },
        {
            label: 'Versions',
            path: `${basePath}/versions`,
            icon: FileStack,
        },
        {
            label: 'Assets',
            path: `${basePath}/collections`,
            icon: Package,
            matchPaths: [
                `${basePath}/templates`,
                `${basePath}/collections`,
                `${basePath}/stickers`,
                `${basePath}/backgrounds`,
                `${basePath}/filters`,
                `${basePath}/colors`,
                `${basePath}/fonts`,
                `${basePath}/audios`,
            ],
        },
        {
            label: 'Brand Context',
            path: `${basePath}/context`,
            icon: FileText,
        },
        {
            label: 'Team',
            path: `${basePath}/team`,
            icon: Users,
        },
        {
            label: 'Domains',
            path: `${basePath}/domains`,
            icon: Globe,
        },
        {
            label: 'Payments',
            path: `${basePath}/payments`,
            icon: CreditCard,
        },
        {
            label: 'AI Actions',
            path: `${basePath}/ai-actions`,
            icon: Sparkles,
        },
    ];

    const isTabActive = (tab: BrandTab): boolean => {
        if (tab.path === basePath) {
            return location.pathname === basePath;
        }

        return location.pathname === tab.path || (tab.matchPaths?.some((p) => location.pathname === p) ?? false);
    };

    return (
        <div className="border-b bg-card">
            <div className="flex items-center gap-1 px-6">
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = isTabActive(tab);

                    return (
                        <Link
                            key={tab.label}
                            to={tab.path}
                            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                                isActive
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-muted-foreground hover:border-muted hover:text-foreground'
                            }`}
                        >
                            <Icon className="h-4 w-4" />
                            {tab.label}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
